import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BellAlertIcon,
  ClipboardDocumentIcon,
  MapPinIcon,
  PhoneIcon,
  ShieldExclamationIcon,
  SpeakerWaveIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { SosContact, sosService } from '../services/sos.service';

const MESSAGE_KEY = 'lgc_sos_message_template';
const NOTE_KEY = 'lgc_sos_quick_note';

const defaultMessage =
  'SOS. I need immediate help. Please call me and track my current location. This alert was triggered from LocalAlly.';

const normalizePhone = (phone: string) => phone.replace(/[^\d+]/g, '');
const getContactId = (contact: SosContact) =>
  String(
    contact.id ||
      (typeof contact._id === 'string'
        ? contact._id
        : (contact._id as { $oid?: string } | undefined)?.$oid) ||
      '',
  ).trim();

const SafetySOSPage: React.FC = () => {
  const { user } = useAuth();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const roleLabel = currentUser?.role === 'TEACHER' ? 'teacher' : 'student';
  const fullName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ').trim() || 'A LocalAlly user';

  const [trustedContacts, setTrustedContacts] = useState<SosContact[]>([]);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactRelation, setContactRelation] = useState('');
  const [messageTemplate, setMessageTemplate] = useState(defaultMessage);
  const [quickNote, setQuickNote] = useState('');
  const [locationLabel, setLocationLabel] = useState('Location not fetched yet.');
  const [locationUrl, setLocationUrl] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [alarmActive, setAlarmActive] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  useEffect(() => {
    const storedMessage = localStorage.getItem(MESSAGE_KEY);
    const storedNote = localStorage.getItem(NOTE_KEY);

    if (storedMessage && storedMessage.trim()) {
      setMessageTemplate(storedMessage);
    }

    if (storedNote) {
      setQuickNote(storedNote);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(MESSAGE_KEY, messageTemplate);
  }, [messageTemplate]);

  useEffect(() => {
    localStorage.setItem(NOTE_KEY, quickNote);
  }, [quickNote]);

  useEffect(() => {
    return () => {
      if (oscillatorRef.current) {
        oscillatorRef.current.stop();
        oscillatorRef.current.disconnect();
      }
      gainNodeRef.current?.disconnect();
      void audioContextRef.current?.close();
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadContacts = async () => {
      setContactsLoading(true);
      try {
        const data = await sosService.listContacts();
        if (!active) return;
        setTrustedContacts(data);
      } catch (error) {
        console.error('Failed to load SOS contacts', error);
        if (!active) return;
        setTrustedContacts([]);
        setStatusMessage('Unable to load trusted contacts right now.');
      } finally {
        if (active) {
          setContactsLoading(false);
        }
      }
    };

    void loadContacts();

    return () => {
      active = false;
    };
  }, []);

  const composedMessage = useMemo(() => {
    const parts = [
      messageTemplate.trim() || defaultMessage,
      `Name: ${fullName}`,
      `Role: ${roleLabel}`,
      `Time: ${new Date().toLocaleString()}`,
      `Location: ${locationUrl || locationLabel}`,
      quickNote.trim() ? `Note: ${quickNote.trim()}` : '',
    ].filter(Boolean);

    return parts.join('\n');
  }, [fullName, locationLabel, locationUrl, messageTemplate, quickNote, roleLabel]);

  const fetchLocation = async () => {
    if (!navigator.geolocation) {
      setStatusMessage('Live location is not supported in this browser.');
      return;
    }

    setLocationLoading(true);
    setStatusMessage(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const mapUrl = `https://maps.google.com/?q=${latitude},${longitude}`;
        setLocationUrl(mapUrl);
        setLocationLabel(`Lat ${latitude.toFixed(5)}, Lng ${longitude.toFixed(5)} | accuracy ${Math.round(accuracy)}m`);
        setLocationLoading(false);
        setStatusMessage('Live location captured.');
      },
      (error) => {
        setLocationLoading(false);
        if (error.code === error.PERMISSION_DENIED) {
          setStatusMessage('Location permission was denied. Enable it and try again.');
          return;
        }
        setStatusMessage('Unable to fetch your location right now.');
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  };

  const copyAlert = async () => {
    try {
      await navigator.clipboard.writeText(composedMessage);
      setStatusMessage('SOS message copied.');
    } catch {
      setStatusMessage('Clipboard copy failed in this browser.');
    }
  };

  const shareAlert = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Emergency SOS',
          text: composedMessage,
        });
        setStatusMessage('SOS message opened in share sheet.');
        return;
      } catch {
        setStatusMessage('Share cancelled.');
        return;
      }
    }

    await copyAlert();
  };

  const toggleAlarm = async () => {
    if (alarmActive) {
      oscillatorRef.current?.stop();
      oscillatorRef.current?.disconnect();
      oscillatorRef.current = null;
      gainNodeRef.current?.disconnect();
      gainNodeRef.current = null;
      setAlarmActive(false);
      setStatusMessage('Alarm stopped.');
      return;
    }

    const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) {
      setStatusMessage('Alarm is not supported in this browser.');
      return;
    }

    const context = audioContextRef.current || new AudioContextCtor();
    audioContextRef.current = context;
    await context.resume();

    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    oscillator.frequency.linearRampToValueAtTime(660, context.currentTime + 0.35);
    oscillator.frequency.linearRampToValueAtTime(880, context.currentTime + 0.7);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, context.currentTime + 0.05);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillatorRef.current = oscillator;
    gainNodeRef.current = gain;
    setAlarmActive(true);
    setStatusMessage('Alarm started.');
  };

  const addTrustedContact = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!contactName.trim() || !contactPhone.trim()) {
      setStatusMessage('Contact name and phone are required.');
      return;
    }

    try {
      const created = await sosService.createContact({
        name: contactName.trim(),
        phone: contactPhone.trim(),
        relation: contactRelation.trim() || undefined,
      });
      setTrustedContacts((current) => [created, ...current]);
      setContactName('');
      setContactPhone('');
      setContactRelation('');
      setStatusMessage('Trusted contact saved.');
    } catch (error) {
      console.error('Failed to save SOS contact', error);
      setStatusMessage('Unable to save trusted contact right now.');
    }
  };

  const removeTrustedContact = async (contactId: string) => {
    if (!contactId) return;
    try {
      await sosService.deleteContact(contactId);
      setTrustedContacts((current) => current.filter((contact) => getContactId(contact) !== contactId));
      setStatusMessage('Trusted contact removed.');
    } catch (error) {
      console.error('Failed to delete SOS contact', error);
      setStatusMessage('Unable to remove trusted contact right now.');
    }
  };

  const sendSms = (phone: string) => {
    const target = normalizePhone(phone);
    window.open(`sms:${target}?body=${encodeURIComponent(composedMessage)}`, '_self');
  };

  const callNumber = (phone: string) => {
    const target = normalizePhone(phone);
    window.open(`tel:${target}`, '_self');
  };

  const openWhatsApp = (phone: string) => {
    const target = normalizePhone(phone).replace(/^\+/, '');
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(composedMessage)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(190,24,93,0.16),_transparent_28%),linear-gradient(180deg,_#fff1f2_0%,_#fff7ed_42%,_#ffffff_100%)]">
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_360px]">
          <div className="overflow-hidden rounded-[2rem] border border-rose-200 bg-[linear-gradient(135deg,_rgba(136,19,55,0.98)_0%,_rgba(190,24,93,0.94)_48%,_rgba(249,115,22,0.88)_100%)] p-6 text-white shadow-[0_32px_120px_-54px_rgba(159,18,57,0.75)] sm:p-8">
            <span className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-rose-100">
              Women Safety SOS
            </span>
            <h1 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl">
              Emergency support for female {roleLabel}s on LocalAlly.
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-rose-50 sm:text-base">
              Use this space to reach trusted contacts fast, capture your live location, trigger a loud alarm, and send a ready-to-share SOS message without hunting through multiple apps.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {[
                { label: 'Fast contact', value: 'Call, SMS, WhatsApp' },
                { label: 'Location', value: locationUrl ? 'Live location ready' : 'Fetch on demand' },
                { label: 'Preparedness', value: trustedContacts.length ? `${trustedContacts.length} trusted contacts` : 'Add trusted contacts' },
              ].map((item) => (
                <div key={item.label} className="rounded-[1.5rem] border border-white/15 bg-white/10 px-4 py-4 backdrop-blur">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-100">{item.label}</p>
                  <p className="mt-3 text-lg font-semibold text-white">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  void fetchLocation();
                }}
                disabled={locationLoading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:opacity-60"
              >
                <MapPinIcon className="h-5 w-5" aria-hidden="true" />
                {locationLoading ? 'Fetching location...' : 'Get live location'}
              </button>
              <button
                type="button"
                onClick={() => {
                  void shareAlert();
                }}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <ShieldExclamationIcon className="h-5 w-5" aria-hidden="true" />
                Share SOS message
              </button>
              <button
                type="button"
                onClick={() => {
                  void toggleAlarm();
                }}
                className={`inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold transition ${
                  alarmActive ? 'bg-amber-300 text-slate-950 hover:bg-amber-200' : 'border border-white/20 bg-white/10 text-white hover:bg-white/15'
                }`}
              >
                <SpeakerWaveIcon className="h-5 w-5" aria-hidden="true" />
                {alarmActive ? 'Stop alarm' : 'Start alarm'}
              </button>
            </div>

            {statusMessage && (
              <div className="mt-5 rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white">
                {statusMessage}
              </div>
            )}
          </div>

          <aside className="space-y-4">
            <div className="rounded-[1.75rem] border border-rose-200 bg-white p-5 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)]">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-600">Immediate steps</p>
              <div className="mt-4 space-y-3">
                {[
                  'Fetch live location first so your outgoing SOS includes a map link.',
                  'Use the alarm if you need to draw nearby attention immediately.',
                  'Keep at least two trusted contacts saved before you need them.',
                ].map((item) => (
                  <div key={item} className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-950">
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[1.75rem] border border-amber-200 bg-amber-50 p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">Important</p>
              <p className="mt-3 text-sm leading-6 text-amber-950">
                This tool speeds up contact and sharing. It does not automatically notify police, platform admins, or emergency services.
              </p>
            </div>

            <div className="rounded-[1.75rem] border border-slate-200 bg-slate-950 p-5 text-white shadow-[0_24px_80px_-52px_rgba(15,23,42,0.55)]">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-200">Platform</p>
              <p className="mt-3 text-lg font-semibold">Need your regular workspace?</p>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                Go back to your dashboard after saving contacts and your message template.
              </p>
              <Link
                to={currentUser?.role === 'TEACHER' ? '/teacher/dashboard' : '/student/dashboard'}
                className="mt-5 inline-flex items-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
              >
                Return to dashboard
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_0.9fr]">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)] sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Trusted contacts</p>
                <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">One-tap reachout list</h2>
              </div>
              <UserGroupIcon className="h-10 w-10 text-rose-500" aria-hidden="true" />
            </div>

            <form onSubmit={addTrustedContact} className="mt-6 grid gap-4 md:grid-cols-2">
              <input
                value={contactName}
                onChange={(event) => setContactName(event.target.value)}
                placeholder="Contact name"
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
              />
              <input
                value={contactPhone}
                onChange={(event) => setContactPhone(event.target.value)}
                placeholder="Phone number"
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
              />
              <input
                value={contactRelation}
                onChange={(event) => setContactRelation(event.target.value)}
                placeholder="Relation or label"
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white md:col-span-2"
              />
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-2xl bg-rose-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 md:col-span-2"
              >
                Save trusted contact
              </button>
            </form>

            <div className="mt-6 space-y-4">
              {contactsLoading ? (
                <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  Loading trusted contacts...
                </div>
              ) : trustedContacts.length === 0 ? (
                <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No trusted contacts saved yet.
                </div>
              ) : (
                trustedContacts.map((contact) => (
                  <div
                    key={getContactId(contact) || `${contact.name}-${contact.phone}`}
                    className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-lg font-semibold text-slate-950">{contact.name}</p>
                        <p className="mt-1 text-sm text-slate-600">{contact.phone}</p>
                        {contact.relation && <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{contact.relation}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          void removeTrustedContact(getContactId(contact));
                        }}
                        className="rounded-full border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => callNumber(contact.phone)}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        <PhoneIcon className="h-4 w-4" aria-hidden="true" />
                        Call
                      </button>
                      <button
                        type="button"
                        onClick={() => sendSms(contact.phone)}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        <BellAlertIcon className="h-4 w-4" aria-hidden="true" />
                        SMS
                      </button>
                      <button
                        type="button"
                        onClick={() => openWhatsApp(contact.phone)}
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                      >
                        WhatsApp
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)]">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Prepared message</p>
              <textarea
                value={messageTemplate}
                onChange={(event) => setMessageTemplate(event.target.value)}
                className="mt-4 min-h-[150px] w-full rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
              />
              <textarea
                value={quickNote}
                onChange={(event) => setQuickNote(event.target.value)}
                placeholder="Optional note: vehicle number, landmark, unsafe situation, etc."
                className="mt-4 min-h-[100px] w-full rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
              />
              <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Current location payload</p>
                <p className="mt-2 text-sm text-slate-700">{locationLabel}</p>
                {locationUrl && (
                  <a
                    href={locationUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex text-sm font-semibold text-rose-600 hover:text-rose-700"
                  >
                    Open map link
                  </a>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    void copyAlert();
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                  Copy full SOS
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void shareAlert();
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                >
                  <ShieldExclamationIcon className="h-4 w-4" aria-hidden="true" />
                  Share / export
                </button>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-slate-950 p-6 text-white shadow-[0_24px_80px_-52px_rgba(15,23,42,0.55)]">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-200">Preview</p>
              <pre className="mt-4 whitespace-pre-wrap break-words rounded-[1.5rem] border border-white/10 bg-white/5 px-4 py-4 text-sm leading-6 text-slate-200">
                {composedMessage}
              </pre>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SafetySOSPage;
